<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('expenseCategories.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('expenseCategories.form.categoryName') }}</label>
              <input type="text" class="form-control" v-model="form.category_name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('expenseCategories.form.description') }}</label>
              <input type="text" class="form-control" v-model="form.description" />
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('expenseCategories.form.update') : $t('expenseCategories.form.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">
            {{ $t('expenseCategories.form.cancel') }}
          </button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-database me-2"></i> {{ $t('expenseCategories.list.title') }}
      </h5>

      <div v-if="categories.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('expenseCategories.list.id') }}</th>
              <th>{{ $t('expenseCategories.list.name') }}</th>
              <th>{{ $t('expenseCategories.list.desc') }}</th>
              <th class="text-center">{{ $t('expenseCategories.list.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cat in categories" :key="cat.id">
              <td>{{ cat.id }}</td>
              <td>{{ cat.category_name }}</td>
              <td>{{ cat.description || '-' }}</td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadCategory(cat)">
                  {{ $t('expenseCategories.list.edit') }}
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="deleteCategory(cat.id)">
                  {{ $t('expenseCategories.list.delete') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('expenseCategories.list.noData') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import axios from '@/api'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast()

const categories = ref([])
const editingId = ref(null)

const form = ref({
  category_name: '',
  description: ''
})

const fetchCategories = async () => {
  try {
    const { data } = await axios.get('/Financial/expense-categories')
    categories.value = data
  } catch (err) {
    toast.error(t('expenseCategories.messages.loadError'))
  }
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await axios.put(`/Financial/expense-categories/${editingId.value}`, form.value)
      toast.success(t('expenseCategories.messages.updateSuccess'))
    } else {
      await axios.post('/Financial/expense-categories', form.value)
      toast.success(t('expenseCategories.messages.addSuccess'))
    }
    resetForm()
    fetchCategories()
  } catch (err) {
    toast.error(t('expenseCategories.messages.saveError'))
  }
}

const loadCategory = (cat) => {
  editingId.value = cat.id
  form.value = {
    category_name: cat.category_name,
    description: cat.description
  }
}

const deleteCategory = async (id) => {
  try {
    await axios.delete(`/Financial/expense-categories/${id}`)
    toast.success(t('expenseCategories.messages.deleteSuccess'))
    fetchCategories()
  } catch (err) {
    toast.error(t('expenseCategories.messages.deleteError'))
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    category_name: '',
    description: ''
  }
}

onMounted(fetchCategories)
</script>
